// 1. React and DOM imports
import type { ReactElement, SVGProps } from 'react';

// 2. External libraries
import { Plug, Sparkles, Webhook } from 'lucide-react';

// 3. Internal components
import {
  ChatGptIcon,
  ClaudeCodeIcon,
  ClaudeIcon,
  DiscordIcon,
  GeminiCliIcon,
  N8nIcon,
  OpenAiCodexIcon,
  SlackIcon,
  TelegramIcon,
} from '~/components/common/icons';

// 4. Internal utilities
import { cn } from '~/lib/utils';

/** Inline markup: **bold**, `code`, [text](url) */
export type InlineMarkup = string;

export type InstructionStep =
  | { type: 'text'; content: InlineMarkup }
  | { type: 'command'; label: InlineMarkup; template: string }
  | { type: 'url'; label?: InlineMarkup }
  | { type: 'nested'; intro: InlineMarkup; items: InstructionStep[] };

export interface ClientInstructions {
  alert: InlineMarkup;
  steps: InstructionStep[];
}

export type ConnectionType = string;

/**
 * Frontend-only connection type configuration.
 *
 * Provides icon, label, description, and optional client instructions.
 * Merged with server-provided ConnectionTypeInfo in useConnectionTypes hook.
 *
 * @see MergedConnectionTypeConfig for the combined type
 */
export interface ConnectionTypeConfig {
  /** Display order in creation dialog (lower values appear first) */
  ordinal: number;
  label: string;
  description: string;
  icon: (props?: SVGProps<SVGSVGElement>) => ReactElement;
  clientInstructions?: ClientInstructions;
}

/**
 * Combined server and frontend connection type data.
 *
 * Re-exported from useConnectionTypes hook for convenience.
 */
export type { ConnectionTypeConfig as MergedConnectionTypeConfig } from '~/hooks/useConnectionTypes';

export const CONNECTION_TYPE_CONFIGS: Record<string, ConnectionTypeConfig> = {
  rest: {
    ordinal: 0,
    label: 'REST API',
    description: 'Direct API access using API keys authentication',
    icon: ({ className }: SVGProps<SVGSVGElement> = {}) => (
      <Plug className={cn('size-5', className)} />
    ),
  },
  mcp: {
    ordinal: 1,
    label: 'MCP',
    description: 'Model Context Protocol using API key authentication',
    icon: ({ className }: SVGProps<SVGSVGElement> = {}) => (
      <Sparkles className={cn('size-5', className)} />
    ),
  },
  chatgpt: {
    ordinal: 2,
    label: 'ChatGPT',
    description: 'App for ChatGPT (Web, Mobile, and Desktop)',
    icon: (props?: SVGProps<SVGSVGElement>) => <ChatGptIcon {...props} />,
    clientInstructions: {
      alert: 'To connect ChatGPT, you need to add DeadDrop as a custom app in ChatGPT settings.',
      steps: [
        {
          type: 'text',
          content:
            'In ChatGPT, go to [Settings → Apps → Advanced](https://chatgpt.com/apps#settings/Connectors/Advanced)',
        },
        { type: 'text', content: 'Click **Enable "Developer mode"**' },
        { type: 'text', content: 'Click **Create app**' },
        {
          type: 'nested',
          intro: 'Fill in the form:',
          items: [
            { type: 'text', content: 'Name: **DeadDrop**' },
            { type: 'url', label: 'MCP Server URL:' },
            { type: 'text', content: 'Check **"I understand and want to continue"**' },
            { type: 'text', content: 'Click **Create**' },
          ],
        },
        { type: 'text', content: 'Authorize ChatGPT in DeadDrop' },
      ],
    },
  },
  claude: {
    ordinal: 3,
    label: 'Claude',
    description: 'Connector for Claude (Web, Mobile, and Desktop)',
    icon: (props?: SVGProps<SVGSVGElement>) => <ClaudeIcon {...props} />,
    clientInstructions: {
      alert:
        'To connect Claude, you need to add DeadDrop as a custom connector in Claude settings.',
      steps: [
        {
          type: 'text',
          content:
            'In Claude, go to [Settings → Connectors](https://claude.ai/settings/connectors)',
        },
        { type: 'text', content: 'Click **Add custom connector**' },
        {
          type: 'nested',
          intro: 'Fill in the form:',
          items: [
            { type: 'text', content: 'Name: **DeadDrop**' },
            { type: 'url', label: 'Remote MCP server URL:' },
            { type: 'text', content: 'Click **Add**' },
          ],
        },
        {
          type: 'text',
          content: 'Find the new DeadDrop connector in the list and click **Connect**',
        },
        { type: 'text', content: 'Authorize Claude in DeadDrop' },
      ],
    },
  },
  claude_code: {
    ordinal: 4,
    label: 'Claude Code',
    description: 'OAuth-authenticated MCP for Claude Code CLI',
    icon: (props?: SVGProps<SVGSVGElement>) => <ClaudeCodeIcon {...props} />,
    clientInstructions: {
      alert:
        'To connect Claude Code, you need to add DeadDrop as an MCP server in your Claude Code configuration.',
      steps: [
        { type: 'text', content: 'Open Terminal' },
        {
          type: 'command',
          label: 'Run the following command:',
          template: 'claude mcp add deaddrop {{serverUrl}} --transport http',
        },
        {
          type: 'text',
          content: 'Run `claude` and confirm adding DeadDrop as the new MCP server',
        },
        {
          type: 'text',
          content: 'In Claude Code, run `/mcp`, select **deaddrop**, then **Authenticate**',
        },
        { type: 'text', content: 'Browser will open, authorize Claude Code in DeadDrop' },
      ],
    },
  },
  gemini_cli: {
    ordinal: 5,
    label: 'Gemini CLI',
    description: 'OAuth-authenticated MCP for Gemini CLI',
    icon: (props?: SVGProps<SVGSVGElement>) => <GeminiCliIcon {...props} />,
    clientInstructions: {
      alert:
        'To connect Gemini CLI, you need to add DeadDrop as an MCP server in your Gemini CLI configuration.',
      steps: [
        { type: 'text', content: 'Open Terminal' },
        {
          type: 'command',
          label: 'Run the following command:',
          template: 'gemini',
        },
        {
          type: 'text',
          content: 'Run `gemini` and confirm adding DeadDrop as the new MCP server',
        },
        {
          type: 'text',
          content: 'In Gemini CLI, run `/mcp`, select **deaddrop**, then **Authenticate**',
        },
        { type: 'text', content: 'Browser will open, authorize Gemini CLI in DeadDrop' },
      ],
    },
  },
  openai_codex: {
    ordinal: 6,
    label: 'Codex',
    description: 'OAuth-authenticated MCP for OpenAI Codex CLI',
    icon: (props?: SVGProps<SVGSVGElement>) => <OpenAiCodexIcon {...props} />,
    clientInstructions: {
      alert:
        'To connect Codex, you need to add DeadDrop as an MCP server in your Codex configuration.',
      steps: [
        { type: 'text', content: 'Open Terminal' },
        {
          type: 'command',
          label: 'Run the following command:',
          template: 'codex mcp add deaddrop --url {{serverUrl}}',
        },
        { type: 'text', content: 'Browser will open, authorize OpenAI Codex in DeadDrop' },
        {
          type: 'text',
          content: 'Launch `codex` and run `/mcp` to confirm DeadDrop is connected',
        },
      ],
    },
  },
  webhook: {
    ordinal: 7,
    label: 'Webhook',
    description: 'Invoke webhooks (POST) when new messages arrive',
    icon: ({ className }: SVGProps<SVGSVGElement> = {}) => (
      <Webhook className={cn('size-5', className)} />
    ),
  },
  slack: {
    ordinal: 8,
    label: 'Slack',
    description: 'Send and receive messages in Slack channels',
    icon: (props?: SVGProps<SVGSVGElement>) => <SlackIcon {...props} />,
  },
  discord: {
    ordinal: 9,
    label: 'Discord',
    description: 'Bidirectional Discord server integration',
    icon: (props?: SVGProps<SVGSVGElement>) => <DiscordIcon {...props} />,
  },
  telegram: {
    ordinal: 10,
    label: 'Telegram',
    description: 'Send and receive messages via Telegram bot',
    icon: (props?: SVGProps<SVGSVGElement>) => <TelegramIcon {...props} />,
  },
  n8n: {
    ordinal: 11,
    label: 'n8n',
    description: 'Real-time messaging for n8n workflow automation',
    icon: (props?: SVGProps<SVGSVGElement>) => <N8nIcon {...props} />,
  },
};

/** Returns connection type keys sorted by ordinal for display ordering. */
export function getSortedConnectionTypes(): string[] {
  return Object.entries(CONNECTION_TYPE_CONFIGS)
    .sort(([, a], [, b]) => a.ordinal - b.ordinal)
    .map(([type]) => type);
}

/** Display names for all connection types derived from CONNECTION_TYPE_CONFIGS */
export const CLIENT_TYPE_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(CONNECTION_TYPE_CONFIGS).map(([key, config]) => [key, config.label])
);

/** Returns icon for a connection type. Falls back to a generic plug icon for unknown types. */
export function getConnectionTypeIcon(
  type: ConnectionType,
  props?: SVGProps<SVGSVGElement>
): ReactElement {
  const config = CONNECTION_TYPE_CONFIGS[type];
  if (!config) {
    return <Plug className={cn('size-5', props?.className)} />;
  }
  return config.icon(props);
}
